import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, ClipboardCheck, FileText, Video, MessageSquare, Lock, CheckCircle2, BookOpen, Sparkles } from "lucide-react";
import { Quiz } from "@/components/Quiz";
import { Assignment } from "@/components/Assignment";
import { ReadAlong, InteractiveLesson } from "@/components/LessonExtras";
import { PreviewBanner } from "@/components/PreviewBanner";
import { useAuth } from "@/context/AuthContext";

export default function LessonView() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPreview = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError("");
    setCompleted(false);
    api.get(`/lessons/${lessonId}`)
      .then((r) => {
        setData(r.data);
        if (!isPreview) {
          api.post("/progress/lesson", { lesson_id: lessonId, status: "viewed" })
            .then((res) => setCompleted(res.data.status === "completed"))
            .catch(() => {});
        }
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [lessonId, isPreview]);

  const markComplete = async () => {
    setSaving(true);
    try {
      await api.post("/progress/lesson", { lesson_id: lessonId, status: "completed" });
      setCompleted(true);
      toast.success("Great job! Lesson complete 🎉");
    } catch {
      toast.error("Could not save progress");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="kid-theme flex min-h-screen items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-primary" /></div>;

  if (error) {
    return (
      <div className="kid-theme min-h-screen">
        <div className="mx-auto max-w-lg px-5 py-24 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-accent text-secondary"><Lock className="h-9 w-9" strokeWidth={2.5} /></div>
          <h1 className="mt-6 font-head text-3xl font-black">Locked for Now</h1>
          <p className="mt-3 text-lg font-semibold text-muted-foreground">{error}</p>
          <Link to="/pricing"><Button className="mt-7 rounded-full bg-primary px-8 py-6 text-base font-extrabold tactile hover:bg-primary" data-testid="lesson-unlock-btn">See Enrollment Options</Button></Link>
        </div>
      </div>
    );
  }

  const { lesson, unit, course, assignments = [], extra_practice: extraPractice = [], assessments = [], interactive, read_along: readAlong, attempts = {}, assignment_attempts: assignmentAttempts = {} } = data;
  const themeClass = course?.subject_category === "college_prep" ? "collegeprep-theme" : "kid-theme";
  const videos = lesson.videos || [];
  const scrollToSec = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 150, behavior: "smooth" });
  };

  const tabs = [
    { id: "sec-lesson", label: "Lesson", icon: BookOpen, color: "#3B82F6", show: true, onClick: () => scrollToSec("sec-lesson") },
    { id: "sec-quiz", label: "Quiz", icon: ClipboardCheck, color: "#F59E0B", show: assessments.length > 0, onClick: () => scrollToSec("sec-quiz") },
    { id: "sec-assignment", label: "Assignment", icon: FileText, color: "#22C55E", show: assignments.length > 0, onClick: () => scrollToSec("sec-assignment") },
    { id: "sec-extra", label: "Extra Practice", icon: Sparkles, color: "#0EA5E9", show: extraPractice.length > 0, onClick: () => scrollToSec("sec-extra") },
    { id: "sec-interactive", label: "Play & Learn", icon: Sparkles, color: "#A855F7", show: !!interactive, onClick: () => scrollToSec("sec-interactive") },
    { id: "tab-tutor", label: "Ask the Tutor", icon: MessageSquare, color: "#F97316", show: true, onClick: () => navigate("/tutor", { state: { lessonTitle: lesson.title, courseTitle: course?.title, unitTitle: unit?.title } }) },
  ].filter((t) => t.show);

  return (
    <div className={`${themeClass} min-h-screen`}>
      <div className="mx-auto max-w-4xl px-5 py-10 lg:px-8">
        <PreviewBanner />
        <div className="flex items-center justify-between gap-4">
          <div>
            {course && <Link to={`/course/${course.course_id}`} className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-2 text-sm font-extrabold text-muted-foreground transition-colors hover:text-primary" data-testid="lesson-back-course"><ArrowLeft className="h-4 w-4" strokeWidth={3} />{course.title}</Link>}
            <p className="mt-3 label-caps text-primary">{unit?.title}</p>
          </div>
        </div>

        <h1 className="mt-5 font-head text-4xl font-black leading-tight sm:text-5xl">{lesson.title}</h1>

        <div className="sticky top-[72px] z-30 -mx-5 mt-6 flex flex-wrap items-center gap-2.5 bg-background/90 px-5 py-3.5 backdrop-blur lg:mx-0 lg:px-0" data-testid="lesson-tabs">
          {tabs.map((t) => (
            <button key={t.id} onClick={t.onClick} data-testid={`lesson-tab-${t.label.split(" ")[0].toLowerCase()}`}
              className="tactile flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-extrabold text-white"
              style={{ backgroundColor: t.color }}>
              <t.icon className="h-5 w-5" strokeWidth={2.75} />
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {videos.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><Video className="h-5 w-5" strokeWidth={2.5} /> Watch</div>
            <div className="mt-3 grid gap-4">
              {videos.map((v, i) => {
                const url = v.url || v.video_url || v.youtube_url || "";
                const yt = url.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
                return yt ? (
                  <div key={i} className="aspect-video w-full overflow-hidden rounded-3xl border-2 border-border bg-card shadow-sm">
                    <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${yt[1]}`} title={v.title || `Video ${i + 1}`} allowFullScreen />
                  </div>
                ) : url ? (
                  <a key={i} href={url} target="_blank" rel="noreferrer" className="rounded-2xl border-2 border-border bg-card p-4 font-bold text-primary hover:bg-accent">{v.title || url}</a>
                ) : null;
              })}
            </div>
          </div>
        )}

        <article id="sec-lesson" className="lesson-html mt-10 rounded-3xl border-2 border-border bg-card p-6 shadow-sm sm:p-8" dangerouslySetInnerHTML={{ __html: lesson.instruction_content || "<p>Lesson content coming soon.</p>" }} />

        {readAlong && <ReadAlong data={readAlong} />}
        <div id="sec-interactive">{interactive && <InteractiveLesson data={interactive} />}</div>

        {assignments.length > 0 && (
          <section id="sec-assignment" className="mt-10">
            <h2 className="flex items-center gap-2 font-head text-2xl font-black"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-green-100 text-green-600"><FileText className="h-5 w-5" strokeWidth={2.5} /></span> Assignments</h2>
            <div className="mt-5 space-y-4">
              {assignments.map((a) => (
                <Assignment key={a.assignment_id} assignment={a} lastAttempt={assignmentAttempts[a.assignment_id]} preview={isPreview} accent="#22C55E" />
              ))}
            </div>
          </section>
        )}

        {extraPractice.length > 0 && (
          <section id="sec-extra" className="mt-10">
            <h2 className="flex items-center gap-2 font-head text-2xl font-black"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sky-600"><Sparkles className="h-5 w-5" strokeWidth={2.5} /></span> Extra Practice</h2>
            <div className="mt-5 space-y-4">
              {extraPractice.map((a) => (
                <Assignment key={a.assignment_id} assignment={a} lastAttempt={assignmentAttempts[a.assignment_id]} preview={isPreview} accent="#0EA5E9" />
              ))}
            </div>
          </section>
        )}

        {assessments.length > 0 && (
          <section id="sec-quiz" className="mt-10">
            <h2 className="flex items-center gap-2 font-head text-2xl font-black"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-secondary"><ClipboardCheck className="h-5 w-5" strokeWidth={2.5} /></span> Quizzes</h2>
            <div className="mt-5 space-y-4">
              {assessments.map((a) => (
                <Quiz key={a.assessment_id} assessment={a} lastAttempt={attempts[a.assessment_id]} preview={isPreview} />
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t-2 border-border pt-6">
          <Button variant="outline" onClick={() => navigate(-1)} className="rounded-full border-2 px-6 font-extrabold" data-testid="lesson-back-btn"><ArrowLeft className="mr-2 h-4 w-4" strokeWidth={3} /> Back</Button>
          {isPreview ? (
            <span className="rounded-full bg-muted px-4 py-2 label-caps text-muted-foreground" data-testid="lesson-preview-note">Preview — progress not tracked</span>
          ) : completed ? (
            <span className="flex items-center gap-2 rounded-full bg-green-100 px-5 py-2.5 text-base font-extrabold text-green-600" data-testid="lesson-completed"><CheckCircle2 className="h-6 w-6" strokeWidth={2.5} /> Completed!</span>
          ) : (
            <Button onClick={markComplete} disabled={saving} className="rounded-full bg-[#22C55E] px-7 py-6 text-base font-extrabold text-white tactile hover:bg-[#22C55E]" data-testid="lesson-complete-btn">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle2 className="mr-2 h-5 w-5" strokeWidth={2.5} /> Mark Complete</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
